import { Component, EventEmitter, Input, Optional, Output } from '@angular/core'
import { ControlContainer, NgForm } from '@angular/forms'
import { TranslationService } from '@evologi/shared/util-translation'
import { PrimeNGConfig } from 'primeng/api'

@Component({
  selector: 'op-date-box-ng',
  templateUrl: './date-box-ng.component.html',
  styleUrls: ['./date-box-ng.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class DateBoxNgComponent {
  @Input() placeholder?: string
  @Input() class?: string
  @Input() key?: string
  @Input() minDate?: Date
  @Input() maxDate?: Date
  @Input() displayTime = false
  @Input()
  get value(): Date | null | undefined {
    return this._dateValue
  }
  set value(val: string | Date | null | undefined) {
    // Convert string (ISO) to Date, or keep Date as-is
    if (typeof val === 'string' && val) {
      this._dateValue = new Date(val)
    } else if (val instanceof Date) {
      this._dateValue = val
    } else {
      this._dateValue = null
    }
  }
  private _dateValue: Date | null | undefined = null

  @Input() format = 'dd/MM/yyyy'
  // Style
  @Input() clearButton = true
  @Input() disabled = false

  // Errors
  @Input() required = false
  @Input() requiredError?: string

  @Output() readonly valueChange = new EventEmitter<string | undefined>()

  constructor(
    private primeNGConfig: PrimeNGConfig,
    private translationService: TranslationService,
  ) {
    this.setTranslation()
  }

  setTranslation() {
    // Configure PrimeNG with Italian locale
    this.primeNGConfig.setTranslation({
      firstDayOfWeek: 1,
      dayNames: [
        this.translationService.translate('Sunday'),
        this.translationService.translate('Monday'),
        this.translationService.translate('Tuesday'),
        this.translationService.translate('Wednesday'),
        this.translationService.translate('Thursday'),
        this.translationService.translate('Friday'),
        this.translationService.translate('Saturday'),
      ],
      dayNamesShort: [
        this.translationService.translate('Sunday').slice(0, 3),
        this.translationService.translate('Monday').slice(0, 3),
        this.translationService.translate('Tuesday').slice(0, 3),
        this.translationService.translate('Wednesday').slice(0, 3),
        this.translationService.translate('Thursday').slice(0, 3),
        this.translationService.translate('Friday').slice(0, 3),
        this.translationService.translate('Saturday').slice(0, 3),
      ],
      dayNamesMin: [
        this.translationService.translate('Sunday').slice(0, 1),
        this.translationService.translate('Monday').slice(0, 1),
        this.translationService.translate('Tuesday').slice(0, 1),
        this.translationService.translate('Wednesday').slice(0, 1),
        this.translationService.translate('Thursday').slice(0, 1),
        this.translationService.translate('Friday').slice(0, 1),
        this.translationService.translate('Saturday').slice(0, 1),
      ],
      monthNames: [
        this.translationService.translate('January'),
        this.translationService.translate('February'),
        this.translationService.translate('March'),
        this.translationService.translate('April'),
        this.translationService.translate('May'),
        this.translationService.translate('June'),
        this.translationService.translate('July'),
        this.translationService.translate('August'),
        this.translationService.translate('September'),
        this.translationService.translate('October'),
        this.translationService.translate('November'),
        this.translationService.translate('December'),
      ],
      monthNamesShort: [
        this.translationService.translate('January').slice(0, 3),
        this.translationService.translate('February').slice(0, 3),
        this.translationService.translate('March').slice(0, 3),
        this.translationService.translate('April').slice(0, 3),
        this.translationService.translate('May').slice(0, 3),
        this.translationService.translate('June').slice(0, 3),
        this.translationService.translate('July').slice(0, 3),
        this.translationService.translate('August').slice(0, 3),
        this.translationService.translate('September').slice(0, 3),
        this.translationService.translate('October').slice(0, 3),
        this.translationService.translate('November').slice(0, 3),
        this.translationService.translate('December').slice(0, 3),
      ],
      today: this.translationService.translate('Today'),
      clear: this.translationService.translate('Delete'),
    })
  }

  // Convert DevExtreme format to PrimeNG format
  get primeNgFormat(): string {
    // PrimeNG uses lowercase 'mm' for months, 'MM' for month names
    // Convert 'dd/MM/yyyy' to 'dd/mm/yy' for PrimeNG
    return this.format
      .replace(/yyyy/g, 'yy')
      .replace(/MM/g, 'mm')
      .replace(/dd/g, 'dd')
  }
  get safeMinDate(): Date | undefined {
    return this.minDate instanceof Date ? this.minDate : undefined
  }
  get safeMaxDate(): Date | undefined {
    return this.maxDate instanceof Date ? this.maxDate : undefined
  }

  checkValue(value: Date | null): string | undefined {
    if (value === null) {
      return undefined
    }
    // Always convert Date to ISO string for API compatibility
    return value.toISOString()
  }
}
